import React, { useState } from 'react';

import { UiButton } from '../ui/ui-button';

type InputProps = {
  org: Org;
};

const TEAM_ENGINE_MESSAGING_APP_ID = '789421076374844';
const TEAM_ENGINE_MESSAGING_LOGIN_CONFIG_ID = '1438533523460093';

(window as any).fbAsyncInit = () => {
  FB.init({
    appId: TEAM_ENGINE_MESSAGING_APP_ID,
    version: 'v18.0',
  });
};

/**
 * This handles the intra-window messaging that comes with Facebook's login process. The login is handled in a different
 * window, and uses the `message` event to pass the results to our handlers here.
 *
 * https://teamengine.atlassian.net/wiki/spaces/BB/pages/3396239376/PROD-3820+Integration+with+WhatsApp#Allowing-customers-to-sign-in-to-Meta-and-set-up-a-WhatsApp-profile
 */
const embeddedSignUpInfoListener = (event: MessageEvent<any>) => {
  // This handler only cares about messages from Facebook - it's possible that other things use this messaging bus for other
  // pop up situations, and we should ignore them
  if (event.origin !== 'https://www.facebook.com') return;

  // Wrap this logic in a try/catch so that in the event that we mishandle something here, we can silently fail
  try {
    const data = JSON.parse(event.data);
    // if user finishes the Embedded SignUp flow and the popup window closes
    if (data.type === 'WA_EMBEDDED_SIGNUP') {
      if (data.event === 'FINISH') {
        const { phone_number_id: phoneNumberId, waba_id: wabaId } = data.data;
        console.log({ phoneNumberId, wabaId });
        // TODO: Once our app has been approved, then we can make a request here to our backend, which will make a request to Twilio to setup this account with a WhatsApp messenger
      } else {
        // If we got here, it means that the Facebook login either hit an error, or in a more likely case, exited the
        // wizard early. We should probably (1) track how many users start, but don't get through the sign up, and (2)
        // show in the UI that the flow was cancelled (just to make them feel good about not having completed the sign up
        // flow)
        const { current_step: currentStep } = data.data;
        console.log(`User did not finish ESU. Last step: ${currentStep}`);
      }
    }
  } catch (e) {
    // TODO: Capture these errors
    console.error(e);
  }
};

// listen for embedded sign up events
window.addEventListener('message', embeddedSignUpInfoListener);

export const WhatsAppConfiguration = ({ org }: InputProps) => {
  const [isInitialized, setIsInitialized] = useState<boolean>(Boolean((window as any).FB));

  const handleRef = (node: HTMLDivElement) => {
    if (!node || isInitialized) {
      return;
    }
    // Weirdly we have to go around react to actually use a script
    // tag. If you just put it in the JSX it won't actually fetch the file
    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.setAttribute('src', `https://connect.facebook.net/en_US/sdk.js`);
    s.setAttribute('crossorigin', `anonymous`);
    s.onload = () => {
      setIsInitialized(true);
    };

    if (node.hasChildNodes()) {
      node.replaceChildren(s);
    } else {
      node.appendChild(s);
    }
  };

  /**
   * Uses the global FB object to login the user. The configuration Id tells Facebook that this login is for someone to
   * use with their business account
   */
  const launchEmbeddedSignUp = () => {
    if (!FB) {
      // Shouldn't get here, but technically possible
      return;
    }

    FB.login(
      () => {
        // No-Op since we are using Twilio's APIs
      },
      // Taken from Twilio's documentation, which we have a copy of at
      // https://teamengine.atlassian.net/wiki/spaces/BB/pages/3396239376/PROD-3820+Integration+with+WhatsApp#Allowing-customers-to-sign-in-to-Meta-and-set-up-a-WhatsApp-profile
      {
        config_id: TEAM_ENGINE_MESSAGING_LOGIN_CONFIG_ID,
        auth_type: 'rerequest', // avoids "user is already logged" in errors if users click the button again before refreshing the page
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          sessionInfoVersion: 2, // required to get WABA ID
          // TODO: Once our app is approved by meta, we need to include this in the field so we can pass it to Twilio
          // setup: {
          //   solutionID: SOLUTION_ID,
          // }
        },
      },
    );
  };

  // TODO: Track whether or not the user is setup in the database and hide this button if the are
  return (
    <div className="u-maxHeight-400px u-minHeight-200px u-overflow-auto">
      <div ref={handleRef} />
      <UiButton
        loading={!isInitialized}
        onClick={launchEmbeddedSignUp}
        className="login-with-facebook-button"
        color="inherit"
      >
        Login with Facebook
      </UiButton>
    </div>
  );
};
